import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { ToastrService } from 'ngx-toastr';
import {
  Tramite,
  Microproceso,
  HistorialMicroproceso,
  ConsultaTramite,
  Filtro,
  EstructuraEspecifica,
  ValoresEspecificos,
  ModeloBase,
  Etapas
} from '@app/shared';
import { Logger, EndpointService } from '@app/core';

const log = new Logger('FlowService');

@Injectable()
export class FlowService {
  constructor(private http: HttpClient, private toastr: ToastrService, private api: EndpointService) {}

  /**
   * Retorna los tramites por default con sus actividades.
   *
   * @param {number} idDefaultTramite
   * @returns {Observable<ConsultaTramite[]>}
   * @memberof FlowService
   */
  getInstanceDefaultTramites(idDefaultTramite: number, filters?: any): Observable<ConsultaTramite[]> {
    return new Observable(obs => {
      const dataForm = {
        id: idDefaultTramite
      };
      if (filters) {
        dataForm['filtros'] = filters;
      }
      this.http.post<ConsultaTramite[]>(this.api.get('instanceDefaultTramite'), dataForm).subscribe(
        instances => {
          obs.next(instances);
          obs.complete();
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Retorna una instancia de un microproceso.
   *
   * @param {number} idTarea
   * @returns {Observable<Microproceso>}
   * @memberof FlowService
   */
  getMicroProcess(idTarea: number): Observable<Microproceso> {
    return new Observable(obs => {
      this.http.get<Microproceso>(this.api.get('microprocesInstance', [idTarea])).subscribe(
        instance => {
          obs.next(instance);
          obs.complete();
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Retorna una instancia de un tramite por default.
   *
   * @param {number} idTramite
   * @returns {Observable<Tramite>}
   * @memberof FlowService
   */
  getInstance(idTramite: number): Observable<Tramite> {
    return new Observable(obs => {
      this.http.get<Tramite>(this.api.get('instanceTramite', [idTramite])).subscribe(
        instance => {
          obs.next(instance);
          obs.complete();
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

  /**
   * Crea una nueva intancia partiendo de un trámite por default.
   *
   * @param {number} idDefaultTramite
   * @returns {Observable<number>}
   * @memberof FlowService
   */
   newInstance(idDefaultTramite: number): Observable<any> {
    return new Observable(obs => {
      this.http
        .post<{ instancia_tramite: string }>(this.api.get('instanceTramite'), {
          tramite: idDefaultTramite
        })
        .subscribe(
          data => {
            const id = Number(data.instancia_tramite);
            obs.next(data);
            obs.complete();
            this.toastr.success(`El trámite fue creado correctamente con el código: ${id}`, 'Operación exitosa');
          },
          err => {
            obs.error(err);
          }
        );
    });
  }

  /**
   * Consulta el historial de versiones de un microproceso
   *
   * @param {number} idTask
   * @returns {Promise<HistorialMicroproceso[]>}
   * @memberof FlowService
   */
  getHistoryMicroproces(idTask: number): Promise<HistorialMicroproceso[]> {
    return new Promise((resolve, reject) => {
      this.http.get<HistorialMicroproceso[]>(this.api.get('historyMicroproces', [idTask])).subscribe(
          historyMicroproces => {
            resolve(historyMicroproces);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * Consulta los filtros para la consulta de el tramite
   *
   * @param {number} idTramite
   * @returns {Promise<Filtro>}
   * @memberof FlowService
   */
  getFiltersShearchTramite(idTramite: number): Promise<Filtro> {
    return new Promise((resolve, reject) => {
      this.http.get<Filtro>(this.api.get('filterTramiteParameter', [idTramite])).subscribe(
          filter => {
            resolve(filter);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * Consulta una maestra sus columnas en especifico
   *
   * @param {EstructuraEspecifica} structure
   * @returns {Promise<ValoresEspecificos[]>}
   * @memberof FlowService
   */
  getColumnsSpecificMasterTable(
    structure: EstructuraEspecifica,
  ): Promise<ValoresEspecificos[]> {
    return new Promise((resolve, reject) => {
      const formData = {
        estructura: structure
      };
      this.http.post<ValoresEspecificos[]>(this.api.get('masterTableSpecificInformation'), formData).subscribe(
          specificValues => {
            resolve(specificValues);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * Genera la creacion de la secundaria
   *
   * @param {number} idTramite
   * @param {*} dataForm
   * @returns {Promise<boolean>}
   * @memberof FlowService
   */
  startSecondary(idTramite: number, dataForm: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      this.http
        .put<boolean>(this.api.get('instanceSecondaryWorkflow', [idTramite]), dataForm).subscribe(
          res => {
            resolve(true);
            this.toastr.success('Secundaria iniciada correctamente', 'Operación exitosa');
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * Cancelación de una secundaria
   *
   * @param {number} idSecondary
   * @param {*} reason
   * @returns {Promise<boolean>}
   * @memberof FlowService
   */
  cancelSecondary(idSecondary: number, reason: any): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const dataForm = {
        secundaria: idSecondary,
        motivo: reason
      };
      this.http
        .post<boolean>(this.api.get('instanceSecondaryCancelation'), dataForm).subscribe(
          res => {
            this.toastr.success('Secundaria cancelada correctamente', 'Operación exitosa');
            resolve(true);
          },
          err => {
            reject(err);
          }
        );
    });
  }

  /**
   * retorna la informacion del semaforo con los tiempos del tramite
   *
   * @param {number} idTramite
   * @return {*}  {Observable<Etapas>}
   * @memberof FlowService
   */
  getStages(idTramite: number):Observable<Etapas>{
    return new Observable(obs => {
      this.http.get<Etapas>(this.api.get('stageInstanceTimes', [idTramite])).subscribe(
        stage => {
         obs.next(stage);
        },
        err => {
          obs.error(err);
        }
      );
    });
  }

} // End class



// WEBPACK FOOTER //
// ./src/app/flow/shared/flow.service.ts